/**
 * Class overview (A2 session planning): one quiet summary of this class's
 * plan as the teaching team is shaping it. Its face names the class, its
 * place in the course and its timing; the body, behind disclosure, holds the
 * purpose, the activity sequence, readings and materials, and coursework.
 *
 * It owns nothing: the plan's draft owner supplies local edits and their save
 * state, every row visits its record's own owner, and its place in the course
 * is the Desk Outline's recorded session -> topic join, in its words.
 */
import { useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { teachingDraftsKey } from "@/components/assignments/coursework/teaching-draft-model";
import { useCourseContent } from "@/components/courses/content/use-course-content";
import { useMaterialLibrary } from "@/components/courses/materials/use-material-library";
import { draftActivities, draftMinutes, planTimingFigure, type DraftActivity } from "@/components/courses/unit-planner";
import { objectiveLines, type UnitDraftState } from "@/components/courses/use-unit-draft";
import type { FactBox, VisitAddress } from "@/components/ui/box.schema";
import { BentoVisit } from "@/components/ui/box-visit-context";
import { useAuth } from "@/contexts/AuthContext";
import type { SessionWorkspace, TeachingSessionAssignment } from "@/lib/api/sessionPortal";
import { teachingDraftsApi } from "@/lib/api/teachingDrafts";

import type { SessionPlanParts } from "../ClassPlanCard";
import { topicForSession } from "./use-session-topic-box";
import { activityNumber, classCoursework, classResources, type OverviewWork } from "./class-overview-model";
import { useUnitProjection } from "./use-unit-projection";

export const OVERVIEW_COPY = {
  label: "Class overview",
  job: "See this class's purpose, running order, readings and coursework together, as the plan stands now.",
  open: "Show overview",
  place: "Place in the course",
  purpose: "Purpose",
  noPurpose: "No objective written yet.",
  sequence: "Running order",
  noActivities: "No activities planned yet.",
  resources: "Readings and materials",
  noResources: "No readings or materials are placed in this class or its topic.",
  resourcesLoading: "Reading readings and materials...",
  resourcesPartial: "Some readings or materials couldn't be read, so this list may be incomplete.",
  coursework: "Coursework",
  noCoursework: "No coursework is placed in this class or begun from its activities.",
  courseworkPartial: "Some coursework couldn't be read, so this list may be incomplete.",
  topicUnknown: "Topic couldn't be read.",
  notInTopic: "Not in a topic",
  openTopic: "Open topic",
  outline: "Course outline",
  saving: "Saving",
  unconfirmed: "Save not confirmed",
  notSaved: "Not saved",
  unsaved: "Unsaved changes",
  saved: "Saved",
  status: { private: "Private draft", draft: "Not published", published: "Published", closed: "Closed", archived: "Archived", referenced: null } as Record<OverviewWork["status"], string | null>,
} as const;

/** The plan's save word, from its one draft owner; nothing when it rests saved with no new receipt. */
export function planSaveWord(state: UnitDraftState | undefined): string | null {
  if (!state) return null;
  if (state.busy || state.autosaveArmed) return OVERVIEW_COPY.saving;
  if (state.pending) return OVERVIEW_COPY.unconfirmed;
  if (state.dirty && (state.autosavePaused || state.conflict || state.storageError || state.comparisonUnavailable)) return OVERVIEW_COPY.notSaved;
  if (state.dirty) return OVERVIEW_COPY.unsaved;
  return state.receipt?.kind === "save" || state.receipt?.kind === "create" ? OVERVIEW_COPY.saved : null;
}

const at = () => new Date().toISOString();

export function useClassOverviewBox({ workspace, parts, outlinePath }: { workspace: SessionWorkspace; parts: SessionPlanParts; outlinePath: string }): FactBox<null> | null {
  const host = useContext(BentoVisit);
  const { me } = useAuth();
  const { course, session, viewer } = workspace;
  const unit = useUnitProjection(course.id, parts.unit);
  const live = Boolean(unit && !parts.unavailable);
  const manages = viewer.allowed_actions.includes("manage_session_coursework");
  const content = useCourseContent(course.id, "draft-preview", live);
  const library = useMaterialLibrary(course.id);
  const drafts = useQuery({
    queryKey: teachingDraftsKey(course.id, `${me?.tenant_id}:${me?.user_id}`, 0),
    queryFn: () => teachingDraftsApi.list(course.id, 0), enabled: live && manages && course.term_open, retry: false,
  });
  if (!unit || parts.unavailable) return null;

  // The plan as the team is writing it: the draft owner's local edits, else the saved plan.
  const activities: DraftActivity[] = parts.state?.draft.activities ?? draftActivities(unit.activities);
  const objectives = parts.state ? objectiveLines(parts.state.draft.objectives) : unit.learning_objectives;
  const timing = planTimingFigure(activities, session.starts_at, session.ends_at ?? null);
  const topic = content.data ? topicForSession(content.data, session.id) ?? null : undefined;
  const sequence = workspace.sequence?.state === "ready" ? workspace.sequence.value : null;
  const place = [sequence ? `Session ${sequence.position} of ${sequence.total}` : session.session_no != null ? `Session ${session.session_no}` : null,
    topic === undefined ? (content.isError ? OVERVIEW_COPY.topicUnknown : null) : topic ? `Topic: ${topic.title}` : OVERVIEW_COPY.notInTopic].filter(Boolean).join(" · ");

  const workRows = workspace.plan.coursework_state === "unavailable" ? null : (unit.id === workspace.plan.unit?.id ? (workspace.plan.unit.assignments ?? []).filter((item): item is TeachingSessionAssignment => "coverage" in item) : []);
  const sources = {
    courseId: course.id, unitId: unit.id, activities,
    references: unit.activity_references, links: unit.activity_coursework,
    library: library.isError ? null : library.isLoading ? null : library.items,
    topicReadings: topic === undefined ? null : topic ? content.data!.readings.filter(reading => reading.topic_id === topic.id) : [],
    sessionWork: workRows,
    drafts: !manages || !course.term_open ? [] : drafts.isError ? null : drafts.data?.items ?? null,
  };
  const resources = classResources(sources);
  const work = classCoursework(sources);
  const loadingResources = library.isLoading || content.isLoading;
  const save = planSaveWord(parts.state);

  const go = (address: VisitAddress, row: string) => host?.visit(address, { box: "class-overview", row });
  const link = (title: string, address: VisitAddress, row: string) => host
    ? <button type="button" dir="auto" className="text-start text-ui font-medium text-foreground underline-offset-2 hover:underline focus-visible:underline [overflow-wrap:anywhere]" data-visit-to={address} onClick={() => go(address, row)}>{title}</button>
    : <span dir="auto" className="text-ui font-medium [overflow-wrap:anywhere]">{title}</span>;
  const part = (heading: string, children: ReactNode) => <section className="grid gap-1.5"><h4 className="text-meta font-medium text-muted-foreground">{heading}</h4>{children}</section>;

  return {
    kind: "fact", id: "class-overview", label: OVERVIEW_COPY.label, job: OVERVIEW_COPY.job, owner: { system: "imam" },
    read: { status: "ready", value: null, readAt: at() },
    fold: "choice",
    act: { does: "open", verb: OVERVIEW_COPY.open },
    prose: () => place || OVERVIEW_COPY.label,
    detail: () => [timing.text, save].filter(Boolean).join(" · "),
    body: () => (
      <div className="grid gap-5" data-class-overview>
        {part(OVERVIEW_COPY.place, <p className="text-ui">{place || OVERVIEW_COPY.notInTopic}</p>)}
        {part(OVERVIEW_COPY.purpose, objectives.length
          ? <ul className="grid list-disc gap-1 ps-5 text-ui" dir="auto">{objectives.map((line, index) => <li key={index} className="[overflow-wrap:anywhere]">{line}</li>)}</ul>
          : <p className="text-ui text-muted-foreground">{OVERVIEW_COPY.noPurpose}</p>)}
        {part(OVERVIEW_COPY.sequence, <div className="grid gap-1">
          <p className="font-data text-meta tabular-nums text-muted-foreground" data-semantic="bento.overview.timing">{timing.text}</p>
          {activities.length ? <ol className="grid gap-0.5">{activities.map((activity, index) => {
            const minutes = draftMinutes(activity);
            const linked = unit.activity_coursework?.[activity.id]?.length;
            return <li key={activity.id} className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] gap-x-3 text-ui">
              <span className="font-data text-meta tabular-nums text-muted-foreground">{activityNumber(index)}</span>
              <span className="[overflow-wrap:anywhere]" dir="auto">{activity.label.trim() || "Untitled activity"}{linked ? <span className="text-meta text-muted-foreground"> · {linked} coursework</span> : null}</span>
              <span className="font-data text-meta tabular-nums text-muted-foreground">{minutes === null ? "Untimed" : `${minutes} min`}</span>
            </li>;
          })}</ol> : <p className="text-ui text-muted-foreground">{OVERVIEW_COPY.noActivities}</p>}
        </div>)}
        {part(OVERVIEW_COPY.resources, <div className="grid gap-1.5">
          {resources.rows.length ? <ul className="grid gap-1.5">{resources.rows.map(row => <li key={row.key} className="grid gap-0.5">
            {link(row.title, row.visit, row.key)}
            <span className="text-meta text-muted-foreground">{[row.kind === "reading" ? "Reading" : "Material", ...row.from].join(" · ")}</span>
          </li>)}</ul> : !resources.partial && !loadingResources ? <p className="text-ui text-muted-foreground">{OVERVIEW_COPY.noResources}</p> : null}
          {loadingResources ? <p role="status" className="text-meta text-muted-foreground">{OVERVIEW_COPY.resourcesLoading}</p>
            : resources.partial ? <p role="status" className="text-meta text-status-warning-foreground">{OVERVIEW_COPY.resourcesPartial}</p> : null}
        </div>)}
        {part(OVERVIEW_COPY.coursework, <div className="grid gap-1.5">
          {work.rows.length ? <ul className="grid gap-1.5">{work.rows.map(row => <li key={row.key} className="grid gap-0.5">
            {link(row.title, row.visit, row.key)}
            <span className="text-meta text-muted-foreground">{[OVERVIEW_COPY.status[row.status], ...row.from, row.placement].filter(Boolean).join(" · ")}</span>
          </li>)}</ul> : !work.partial ? <p className="text-ui text-muted-foreground">{OVERVIEW_COPY.noCoursework}</p> : null}
          {work.partial && !drafts.isLoading && <p role="status" className="text-meta text-status-warning-foreground">{OVERVIEW_COPY.courseworkPartial}</p>}
        </div>)}
      </div>
    ),
    exits: topic ? [{ label: OVERVIEW_COPY.openTopic, visit: `topic:${course.id}/${topic.id}` }, { label: OVERVIEW_COPY.outline, to: outlinePath }] : [{ label: OVERVIEW_COPY.outline, to: outlinePath }],
  };
}
